import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { FilmStrip, MagnifyingGlass, Funnel, ArrowLeft } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import { pressable } from '../lib/a11y';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Marketplace = ({ user }) => {
  const navigate = useNavigate();
  const [movies, setMovies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [industryFilter, setIndustryFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchMovies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchMovies = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/movies`);
      setMovies(response.data);
    } catch (error) {
      toast.error('Failed to load movies');
    } finally {
      setLoading(false);
    }
  };

  const filteredMovies = movies.filter(movie => {
    const matchesSearch = movie.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         movie.genre.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filter === 'all' || movie.status === filter;
    const matchesIndustry = industryFilter === 'all' || movie.industry === industryFilter;
    return matchesSearch && matchesFilter && matchesIndustry;
  });

  const getFundingPercentage = (movie) => {
    return Math.min((movie.raised_amount / movie.target_amount) * 100, 100);
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="noise-overlay" />
      
      {/* Navbar */}
      <nav data-web-chrome="nav" className="relative z-10 glass-card sticky top-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-6">
              <button onClick={() => navigate('/')} data-testid="nav-back-btn" className="flex items-center space-x-3">
                <img src="/fanzingy-logo.jpeg" alt="FanZingy" className="h-10 w-10 rounded-full object-cover ring-1 ring-white/10" data-testid="brand-logo" />
            <span className="font-heading text-2xl font-bold tracking-tight">Fan<span className="text-gold">Zingy</span></span>
              </button>
            </div>
            <div className="flex space-x-4">
              {user ? (
                <button
                  data-testid="nav-dashboard-btn"
                  onClick={() => navigate(
                    user.role === 'producer' ? '/producer-dashboard' : 
                    user.role === 'fan' ? '/fan-dashboard' : 
                    '/investor-dashboard'
                  )}
                  className="btn-primary"
                >
                  Dashboard
                </button>
              ) : (
                <button
                  data-testid="nav-login-btn"
                  onClick={() => navigate('/login')}
                  className="btn-primary"
                >
                  Login
                </button>
              )}
            </div>
          </div>
        </div>
      </nav>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-12">
          <h1 className="font-heading text-5xl font-bold mb-4">
            MOVIE <span className="text-gold">MARKETPLACE</span>
          </h1>
          <p className="font-body text-xl text-slate-400 native-hide">
            Explore investment opportunities in upcoming blockbusters
          </p>
        </div>

        {/* Search & Filters */}
        <div className="mb-12 space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <MagnifyingGlass aria-hidden="true" size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                data-testid="marketplace-search-input"
                type="search"
                aria-label="Search movies or genres"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search movies or genres..."
                className="w-full bg-black/20 border border-white/10 focus:border-gold/50 focus:ring-1 focus:ring-gold/50 rounded-sm h-12 text-white placeholder:text-slate-400 pl-12 pr-4 font-body outline-none transition-colors"
              />
            </div>
          </div>

          {/* Industry Filter */}
          <div className="flex gap-2 overflow-x-auto pb-2 native-chip-row" role="group" aria-labelledby="mp-industry-label">
            <span id="mp-industry-label" className="font-body text-sm text-slate-400 flex items-center mr-2">Industry:</span>
            {['all', 'Tollywood', 'Bollywood', 'Kollywood', 'Hollywood'].map((industry) => (
              <button
                key={industry}
                data-testid={`filter-industry-${industry.toLowerCase()}-btn`}
                aria-pressed={industryFilter === industry}
                onClick={() => setIndustryFilter(industry)}
                className={`px-4 py-2 rounded-sm font-body text-sm whitespace-nowrap transition-all ${
                  industryFilter === industry
                    ? 'bg-gold text-black font-bold'
                    : 'bg-white/5 border border-white/10 text-slate-400 hover:bg-white/10'
                }`}
              >
                {industry === 'all' ? 'All' : industry}
              </button>
            ))}
          </div>

          {/* Status Filter */}
          <div className="flex gap-2 overflow-x-auto pb-2 native-chip-row" role="group" aria-labelledby="mp-status-label">
            <span id="mp-status-label" className="font-body text-sm text-slate-400 flex items-center mr-2">Status:</span>
            {['all', 'funding', 'production', 'released'].map((status) => (
              <button
                key={status}
                data-testid={`filter-${status}-btn`}
                aria-pressed={filter === status}
                onClick={() => setFilter(status)}
                className={`px-4 py-2 rounded-sm font-body text-sm uppercase tracking-wide whitespace-nowrap transition-all ${
                  filter === status
                    ? 'bg-gold text-black font-bold'
                    : 'bg-white/5 border border-white/10 text-slate-400 hover:bg-white/10'
                }`}
              >
                {status}
              </button>
            ))}
          </div>
        </div>

        {/* Movies Grid */}
        {loading ? (
          <div role="status" aria-live="polite">
            <span className="sr-only">Loading movies</span>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-8" aria-hidden="true">
              <div className="fz-skeleton md:col-span-8 aspect-[16/9] rounded-lg" />
              <div className="fz-skeleton md:col-span-4 aspect-[2/3] rounded-lg" />
              <div className="fz-skeleton md:col-span-4 aspect-[2/3] rounded-lg" />
            </div>
          </div>
        ) : filteredMovies.length === 0 ? (
          <div className="text-center py-20">
            <FilmStrip aria-hidden="true" size={64} className="text-slate-400 mx-auto mb-4" />
            <p className="font-body text-xl text-slate-400">No movies found</p>
          </div>
        ) : (
          <div data-testid="movies-grid" className="grid grid-cols-1 md:grid-cols-12 gap-8">
            {filteredMovies.map((movie, idx) => {
              const fundingPct = getFundingPercentage(movie);
              const isLarge = idx % 5 === 0;
              
              return (
                <motion.div
                  key={movie.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: idx * 0.05 }}
                  className={`movie-card ${isLarge ? 'md:col-span-8' : 'md:col-span-4'} aspect-[${isLarge ? '16/9' : '2/3'}] bg-gradient-to-br from-purple-900/60 via-slate-900 to-black ring-1 ring-white/10`}
                  {...pressable(() => navigate(`/movie/${movie.id}`), `Open ${movie.title}`)}
                  data-testid={`movie-card-${movie.id}`}
                >
                  <div className="relative w-full h-full">
                    <img
                      src={movie.poster_url}
                      alt=""
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/10" />
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <div className="mb-3">
                        <span className="inline-block px-3 py-1 bg-gold/20 border border-gold/50 rounded-sm text-gold text-xs font-bold uppercase tracking-wide">
                          {movie.genre}
                        </span>
                      </div>
                      <h3 className="font-heading text-2xl font-bold mb-2">{movie.title}</h3>
                      <p className="font-body text-sm text-slate-400 mb-4 line-clamp-2">{movie.description}</p>
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-body text-xs text-slate-400 mb-1">Raised</div>
                          <div className="font-mono text-lg font-bold text-gold tabular-nums">
                            ₹{(movie.raised_amount / 1000).toFixed(0)}K / ₹{(movie.target_amount / 1000).toFixed(0)}K
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-body text-xs text-slate-400 mb-1">Progress</div>
                          <div className="font-mono text-lg font-bold tabular-nums">{fundingPct.toFixed(0)}%</div>
                        </div>
                      </div>
                      <div
                        className="mt-3 h-1 bg-white/10 rounded-full overflow-hidden"
                        role="progressbar"
                        aria-label={`${movie.title} funding progress`}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-valuenow={Math.round(fundingPct)}
                      >
                        <div
                          className="h-full bg-gold transition-all duration-500"
                          style={{ width: `${fundingPct}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Marketplace;